import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import Accordion from './Accordion';

/* ───────────────────────────── helper accordion just for nav */
const NavAccordion = ({ link, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
  
    return (
      <div className="nav-accordion w-full">
        <div className="flex items-center w-full">
          {/* text link – occupies remaining space, stops propagation handled by browser */}
          <div className="flex-1 min-w-0">{link}</div>
  
          {/* chevron button – only this toggles */}
          <button
            className="flex-none"
            aria-expanded={open}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            <svg 
                className={`w-5 h-5 text-green transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                >
                <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 9l-7 7-7-7"
                />
            </svg>
          </button>
        </div>
  
        {open && <div className="mt-4">{children}</div>}
      </div>
    );
  };

const EfficacyNavigation = ({ sections = [], style = ''}) => {
  const navRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [activeLabel, setActiveLabel] = useState("");

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const findActiveLabel = (items, parent = "") => {
        for (const item of items) {
          if (!item) continue;
      
          /* ───────────────────── simple link */
          if (item.__typename === "ContentfulLink" && item.href === location.pathname) {
            return parent ? `${parent} ${item.label}` : item.label;
          }
      
          /* ───────────────────── nav-group */
          if (item.__typename === "ContentfulNavigationGroup") {
            /* 1. singular navLink object */
            if (
              item.navLink &&
              !Array.isArray(item.navLink) &&
              item.navLink.href === location.pathname
            ) {
              return `${item.label}: ${item.navLink.label}`;
            }
      
            /* 2. array of navLink objects */
            if (
              Array.isArray(item.navLink) &&
              item.navLink.some((nav) => nav.href === location.pathname)
            ) {
              const hit = item.navLink.find((nav) => nav.href === location.pathname);
              return `${item.label}: ${hit.label}`;
            }
      
            /* 3. subNav list */
            if (Array.isArray(item.subNav)) {
              for (const sub of item.subNav) {
                /* sub is a simple link */
                if (sub.__typename === "ContentfulLink" && sub.href === location.pathname) {
                  return `${item.label}: ${sub.label}`;
                }
      
                /* sub has its own navLink */
                if (sub.navLink && sub.navLink.href === location.pathname) {
                  return `${item.label}: ${sub.navLink.label}`;
                }
      
                /* nested subNav array one level deeper */
                if (Array.isArray(sub.subNav)) {
                  const nestedHit = sub.subNav.find((n) => n.href === location.pathname);
                  if (nestedHit) {
                    return `${item.label}: ${nestedHit.label}`;
                  }
                }
              }
            }
          }
        }
        return "Select a section";
      };
      

    const newActiveLabel = findActiveLabel(sections);
    setActiveLabel(newActiveLabel);
  }, [location.pathname, sections]);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const renderLink = (item, extraClass = '') => (
    <Link
      key={item.contentfulid || item.href}
      to={item.href}
      className={`block hover:no-underline text-[#000000B3] ${extraClass}`}
      activeClassName="!text-dark_green font-bold"
    >
      {item.label}
    </Link>
  );

  const renderNestedList = (subNav = []) => (
    <ul className="space-y-6 ml-4">
      {subNav.map((nested) => (
        <li key={nested.contentfulid || nested.href}>{renderLink(nested)}</li>
      ))}
    </ul>
  );


  const isActiveLink = (href) => location.pathname === href;
  const containsActive = (item) => {
    if (!item) return false;
  
    // direct link
    if (item.__typename === "ContentfulLink" && isActiveLink(item.href)) return true;
  
    // group's standalone navLink
    if (item.navLink && isActiveLink(item.navLink.href)) return true;
  
    // array navLink at this level
    if (Array.isArray(item.navLink) && item.navLink.some((l) => isActiveLink(l.href))) {
      return true;
    }
  
    // any deeper subNav
    if (Array.isArray(item.subNav) && item.subNav.some(containsActive)) return true;
  
    return false;
  };

  const renderSections = () =>
    sections.map((section) => {
      if (!section) return null;

      if (section.__typename === 'ContentfulLink') {
        return (
          <li key={section.contentfulid} className="my-6 lg:mt-0">
            <Link
              to={section.href}
              className={`text-dark_green lg:text-h4 text-p leading-relaxed ${
                isActiveLink(section.href)
                  ? 'font-bold relative before:absolute before:-left-10 before:top-1/2 before:-translate-y-1/2 before:bg-[#F2994A] before:w-[24px] before:h-[2px] rounded-right-full'
                  : ''
              }`}
            >
              {section.label}
            </Link>
          </li>
        );
      }

      if (section.__typename === 'ContentfulNavigationGroup') {
        return (
          <li key={section.contentfulid} className="xl:mb-8">
            <div
              className={`xl:mb-8 mb-6 text-dark_green lg:text-h4 text-p leading-relaxed ${
                containsActive(section)
                  ? 'font-bold relative before:absolute before:-left-10 before:top-1/2 before:-translate-y-1/2 before:bg-[#F2994A] before:w-[24px] before:h-[2px] rounded-right-full'
                  : ''
              }`}
            >
              {section.label}
            </div>
            <ul className="space-y-6 pl-2 w-full">
              {section.navLink?.map(link => (
                <li key={link.contentfulid}>{renderLink(link)}</li>
              ))}
              {section.subNav?.map((subItem, index) => (
                <li key={subItem.contentfulid || subItem.label} className={`mb-6 ${index === 4 ? 'xl:w-[93px] xl:break-all' : ''}`}>
                  <div className="flex items-center justify-between group">
                    {subItem.navLink ? (
                        <NavAccordion
                            link={renderLink(subItem.navLink)}
                            children={renderNestedList(subItem.subNav)}
                        />
                    ) : renderLink(subItem.navLink || subItem)}
                  </div>
                </li>
              ))}
            </ul>
          </li>
        );
      }
      return null;
    });

  return (
    <div className="xl:ml-[241px]">
      <nav
        ref={navRef}
        className={`xl:flex hidden efficacy-navigation min-h-screen w-[241px] absolute left-0 z-10 flex-col items-start self-stretch ${style}`}
        aria-label="Efficacy navigation"
      >
        <div className="pb-[118px] pt-[68px] pl-[40px] pr-[16px] flex flex-col items-start gap-10 w-full">
          <ul className="space-y-6 w-full">{renderSections()}</ul>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="xl:hidden block">
			  <div className="relative w-full flex justify-center py-[70px] mt-[-70px] mb-[-100px]" ref={dropdownRef}>
                {/* Pill-shaped button */}
                <button
                    className="container-gradient-light !shadow-[0px_10px_50px_0px_#0000001A] backdrop-blur-[30px] w-[calc(100%-48px)] z-10 mx-auto rounded-full bg-white px-8 py-4 flex items-center justify-between focus:outline-none border border-[#E2E8F0]"
                    onClick={() => setOpen(!open)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                >
                    <span className="text-dark_green font-bold lg:text-p text-[14px] truncate">
                        {activeLabel}
                    </span>
                    <svg
                        className={`ml-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                        width="24" height="24" viewBox="0 0 24 24" fill="none"
                    >
                        <path d="M8.12 10.71L12 14.59L15.88 10.71C16.27 10.32 16.9 10.32 17.29 10.71C17.68 11.1 17.68 11.73 17.29 12.12L12.7 16.71C12.31 17.1 11.68 17.1 11.29 16.71L6.7 12.12C6.31 11.73 6.31 11.1 6.7 10.71C7.09 10.32 7.73 10.32 8.12 10.71Z" fill="#F2994A" />
                    </svg>
                </button>
                {/* Dropdown list */}
                {open && (
                    <ul className="absolute left-0 right-0 px-[32px] z-[3] w-[calc(100%-48px)] mx-auto bg-white rounded-all_48 shadow-lg border border-[#E2E8F0] overflow-hidden animate-fadeIn pt-[64px] pb-[24px]" style={{ top: '70px' }}>
                        {renderSections()}
                    </ul>
                )}
                <style jsx>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-8px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.15s ease;
                    }
                `}</style>
            </div>
      </div>
    </div>
    );
};

export default EfficacyNavigation;
